<template>
  <div>
    <div v-if="months.length === 0" class="text-center grey--text py-10">
      {{ emptyText }}
    </div>
    <template v-else>
      <div class="chart-container">
        <line-chart :chart-data="chartData" :options="chartOptions" :styles="chartStyles" />
      </div>
      <p class="caption grey--text text--darken-1 mt-2 mb-0">
        * Mes en curso: todavía parcial.
      </p>
    </template>
  </div>
</template>

<script>
import { Line } from 'vue-chartjs';

// Chart.js pinta en canvas y no lee custom properties: se repiten aquí los
// valores de la paleta de src/styles/variables.scss ($pw-text, $pw-primary,
// $pw-text-faint, $pw-text-muted).
export const TREND_COLORS = {
  text: '#111827',
  primary: '#104B94',
  faint: '#9CA3AF',
  muted: '#6B7280'
};

// Patrones de guion: las series se distinguen por tipo de línea además del
// color, sin usar colores de estado.
export const TREND_DASHES = {
  short: [4, 4],
  long: [10, 5],
  dotted: [2, 3]
};

const MONTH_LABELS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

// Wrapper de vue-chartjs (mismo patrón que ContractMrrTrendDialog): re-renderiza al cambiar los datos.
const LineChart = {
  extends: Line,
  props: ['chartData', 'options'],
  mounted() {
    this.renderChart(this.chartData, this.options);
  },
  watch: {
    chartData: {
      deep: true,
      handler(newData) {
        if (this.$data._chart) this.$data._chart.destroy();
        this.renderChart(newData, this.options);
      }
    }
  }
};

function lineDataset({ label, data, color, dash, hideInLegend = false }) {
  return {
    label,
    hideInLegend,
    data,
    borderColor: color,
    backgroundColor: color,
    pointBackgroundColor: color,
    borderDash: dash,
    borderWidth: 2,
    pointRadius: 4,
    pointHoverRadius: 6,
    lineTension: 0,
    fill: false,
    spanGaps: false
  };
}

export default {
  name: 'B2CTrendChart',
  components: { LineChart },
  props: {
    // [{ month: 'YYYY-MM', partial: boolean }], un punto por mes
    months: { type: Array, default: () => [] },
    // [{ label, data: number[] (null = sin dato), color, dash }]
    series: { type: Array, default: () => [] },
    // 'count' | 'usd'
    valueType: { type: String, default: 'count' },
    // (index) => string[]: líneas extra del tooltip de un mes (p. ej. altas, bajas y neto)
    tooltipDetails: { type: Function, default: null },
    emptyText: { type: String, default: 'Sin datos desde julio 2026' }
  },
  data() {
    // Sin esto el div que envuelve al canvas no toma el alto del contenedor
    // y el gráfico se desborda sobre la tabla.
    return { chartStyles: { height: '100%', position: 'relative' } };
  },
  computed: {
    labels() {
      return this.months.map((m) => {
        const [year, month] = m.month.split('-');
        return `${MONTH_LABELS[Number(month) - 1]} ${year}${m.partial ? '*' : ''}`;
      });
    },
    chartData() {
      return { labels: this.labels, datasets: this.series.map(lineDataset) };
    },
    chartOptions() {
      const format = (v) => (this.valueType === 'usd' ? `US$${this.formatNumber(v)}` : this.formatNumber(v));
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: this.series.length > 1,
          position: 'top',
          align: 'end',
          labels: {
            boxWidth: 24,
            usePointStyle: false,
            filter: (item, data) => !data.datasets[item.datasetIndex].hideInLegend
          }
        },
        scales: {
          xAxes: [{ gridLines: { display: false } }],
          yAxes: [
            {
              ticks: { maxTicksLimit: 6, callback: format },
              gridLines: { color: 'rgba(0,0,0,0.05)', zeroLineColor: 'rgba(0,0,0,0.2)' }
            }
          ]
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: (item, data) => `${data.datasets[item.datasetIndex].label}: ${format(item.yLabel)}`,
            afterBody: (items) =>
              this.tooltipDetails && items.length ? this.tooltipDetails(items[0].index) : []
          }
        }
      };
    }
  },
  methods: {
    formatNumber(n) {
      return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(n) || 0);
    }
  }
};
</script>

<style scoped>
.chart-container {
  position: relative;
  height: 280px;
  width: 100%;
}
</style>
